import React, { type FormEvent, useCallback, useRef } from 'react';

import { DEFAULT_MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_BYTES, HN_SKJEMA_FRONTEND_RESOURCES, VALID_FILE_TYPES } from 'src/constants/constants';
import { SaveCapabilites } from 'src/constants/saveCapabilites';
import { useStepContext } from 'src/contexts/stepContext/useStepContext';
import useAttachmentUtils from 'src/hooks/useAttachmentUtils';
import useFormHasChanges from 'src/hooks/useFormHasChanges';
import { useGetSchemaResources } from 'src/hooks/useGetSchemaResources';
import { useHandleAuthenticationRequirment } from 'src/hooks/useHandleAuthenticationRequirment';
import useHandleLanguageChange from 'src/hooks/useHandleLanguageChange';
import { useRedirectVeilederData } from 'src/hooks/useRedirectVeilederData';
import useValidateAttachments from 'src/hooks/useValidateAttachemnts';
import useCustomMicrowebStepFunctions from 'src/microweb-step/useCustomMicrowebStepFunctions';
import { useResources } from 'src/resources';
import { lagreSkjema, lastMottakere, loadValueSet, sendInnSkjema, abortSkjema } from 'src/store/actions/actionCreators';
import { shouldShowBeforeUnload as shouldShowBeforeUnloadFn } from 'src/store/actions/actions';
import { gotoMinHelse, gotoUrl } from 'src/store/actions/actionUtils';
import { useAppDispatch, useAppSelector } from 'src/store/reducers';
import { kanIkkeLagreError, promptLoginMessage, setSamtaleguid, toggleAttachmentErrorModal } from 'src/store/reducers/uiSlice';
import { getDocumentGuid } from 'src/store/selectors/microwebSelectors';
import { getQueryParams, getSkjemaNameFromUrl, toRelativeUrl } from 'src/util';
import { getLastSaveCapabilitiesValue } from 'src/util/extension';
import { handleFocus } from 'src/util/navigation-utils';
import { trackFormEvent } from 'src/util/trackFormEvent';

import type { QuestionnaireItem } from 'fhir/r4';
import type { HNSkjemaFrontend } from 'src/types/Resources';

import { showPendingChangesModal, resetPendingChanges } from '@helsenorge/core-framework/pending-changes/pending-changes-state';
import {
  visualCheckboxCloudPlugin,
  visualCheckboxCloudOpenChoicePlugin,
  visualCheckboxGroupPlugin,
  visualCheckboxGroupOpenChoicePlugin,
  visualRadioCloudPlugin,
  visualRadioCloudOpenChoicePlugin,
  visualRadioGroupPlugin,
  visualRadioGroupOpenChoicePlugin,
} from '@helsenorge/hn-skjemakomponenter';
import { Refero, type OrgenhetHierarki } from '@helsenorge/refero';

import RenderCustomFormButtons from './customFormButtons/RenderCustomFormButtons';
import HelpButton from './helpComponents/HelpButton';
import HelpElement from './helpComponents/HelpElement';
import Navigator from './navigator/navigator';
import SkjemaLoginButton from './skjemaLoginButton';
import SkjemaTitle from './SkjemaTitle';
import StepViewComponent from './stepView/StepViewComponent';
import { shouldFormBeDisplayedAsStepView } from './stepView/utils';
import TopMessage from './TopMessage/TopMessageComponent';

type Props = {
  timeSkjemaLoadedForUser: number;
  setLastActivity: React.Dispatch<React.SetStateAction<number>>;
  openSidebar: (e: Event | FormEvent<unknown> | undefined) => void;
  lastActivity: number;
  refreshTokenIfExpiringSoon: () => Promise<void>;
};

const RenderSkjema = ({
  timeSkjemaLoadedForUser,
  setLastActivity,
  openSidebar,
  lastActivity,
  refreshTokenIfExpiringSoon,
}: Props): React.JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { isMicrowebStep, isMicroweb, isMicrowebOppgave, showPromptLoginMessageInMicroweb, showValidationSummary } = useAppSelector(
    state => state.microweb
  );
  const { onCompleteMicroweb, sendPendingChangesEvent } = useCustomMicrowebStepFunctions();
  const { setStep } = useStepContext();
  const { validateSave, validateLive, liveErrors } = useValidateAttachments();
  useHandleLanguageChange();
  const uiState = useAppSelector(state => state.refero.ui);
  const formData = useAppSelector(state => state.refero.form.FormData);
  const formDefinition = useAppSelector(state => state.refero.form.FormDefinition);
  const kanMellomlagres = useAppSelector(state => state.refero.ui.kanMellomlagres);
  const userMetadata = useAppSelector(state => state.refero.ui.userMetadata);
  const blockSubmit = useAppSelector(state => state.refero.ui.blockSubmit);
  const returUrl = useAppSelector(state => state.refero.ui.returUrl);
  const documentGuid = useAppSelector(getDocumentGuid);

  const { getAttachmentLink, openAttachment, uploadAttachment, deleteAttachment } = useAttachmentUtils();
  const { authenticationRequirement, isAuthorized, allowAnonymousOrAuthorized } = useHandleAuthenticationRequirment();
  const resources = useResources<HNSkjemaFrontend>(HN_SKJEMA_FRONTEND_RESOURCES);
  const skjemaResources = useGetSchemaResources();
  const hasChanges = useFormHasChanges(sendPendingChangesEvent);

  const shouldPromptLoginMessage = !isMicroweb || (isMicroweb && showPromptLoginMessageInMicroweb);
  const hideValidationSummary = isMicroweb && !showValidationSummary;
  const isStepView = shouldFormBeDisplayedAsStepView(formDefinition);
  const redirectVeilederData = useRedirectVeilederData();
  const queryParams = getQueryParams();

  const attachmentErrorMessage = useCallback(
    (): string | undefined => (liveErrors.length > 0 ? liveErrors.join(', ') : undefined),
    [liveErrors]
  );
  const lastTokenCheckRef = useRef(0);
  const TOKEN_CHECK_THROTTLE_MS = 60000;

  if (!formDefinition || !formDefinition.Content || !resources || !skjemaResources) {
    return null;
  }

  const submitForm = (): void => {
    const lastSaveCapabilityItemValue = getLastSaveCapabilitiesValue(formDefinition?.Content?.item, formData?.Content?.item);
    const itemApprovesSavingOnComplete =
      lastSaveCapabilityItemValue === SaveCapabilites.FinalAndIntermediateVersion ||
      lastSaveCapabilityItemValue === SaveCapabilites.FinalVersionOnly;

    if (lastSaveCapabilityItemValue === SaveCapabilites.NoSaving || (!itemApprovesSavingOnComplete && !uiState.kanLagresEtterInnsending)) {
      dispatch(setSamtaleguid({ samtaleGuid: uiState.samtaleGuid }));

      dispatch(shouldShowBeforeUnloadFn({ show: false }));
      redirectVeilederData(gotoMinHelse);
    } else {
      dispatch(
        sendInnSkjema({
          timeSkjemaLoadedForUser,
          automatiskLagring: false,
          isMicrowebStep,
          isMicrowebOppgave,
          onCompleteMicroweb,
          documentGuid: documentGuid,
          returUrl: returUrl,
        })
      );
    }
  };

  const onSubmit = (): void => {
    const waitForPending = async (): Promise<void> => {
      await dispatch(resetPendingChanges());
      const errors = validateLive();
      if (errors) {
        dispatch(toggleAttachmentErrorModal({ show: true, text: errors }));
      } else {
        if (!uiState.skjemaSendesInn) {
          submitForm();
        }
      }
    };
    waitForPending();
  };

  const onSave = (): void => {
    if (kanMellomlagres) {
      const lastSaveCapabilityItemValue = getLastSaveCapabilitiesValue(formDefinition?.Content?.item, formData?.Content?.item);
      const errors = validateSave();
      if (lastSaveCapabilityItemValue === SaveCapabilites.NoSaving || lastSaveCapabilityItemValue === SaveCapabilites.FinalVersionOnly) {
        dispatch(kanIkkeLagreError());
      } else if (errors) {
        return;
      } else {
        dispatch(lagreSkjema({ timeSkjemaLoadedForUser, automatiskLagring: false }));
      }
    }
  };

  const onCancel = (): void => {
    const returUrlParam = queryParams.get('returURL') ?? undefined;
    if (hasChanges) {
      dispatch(showPendingChangesModal());
    } else if (returUrlParam) {
      const returUrlParamDecoded = decodeURIComponent(returUrlParam);
      gotoUrl(toRelativeUrl(returUrlParamDecoded));
    } else {
      dispatch(resetPendingChanges());
      dispatch(abortSkjema());
    }
  };
  const registerActivity = (): void => {
    const now = Date.now();
    setLastActivity(now);
    if (now - lastTokenCheckRef.current > TOKEN_CHECK_THROTTLE_MS) {
      lastTokenCheckRef.current = now;
      refreshTokenIfExpiringSoon();
    }
  };

  const erSendOgLagreKnapperDisabled = (): boolean => {
    const hideIfRepresentertSubjectOnly = userMetadata?.IsRepresentert && userMetadata?.CanOnlyBePerformedBySubject;
    const hideIfNoTilgang = !userMetadata?.HasTilgang;
    return isAuthorized() && (hideIfRepresentertSubjectOnly || hideIfNoTilgang);
  };

  const trackFormError = (): void => {
    trackFormEvent('site error', getSkjemaNameFromUrl());
  };

  const submitButtonDisabled = !!uiState.loadSkjemaoppgaveWarningMessage || erSendOgLagreKnapperDisabled();

  const fetchReceivers = (successCallback: (receivers: Array<OrgenhetHierarki>) => void, errorCallback: () => void): void => {
    lastMottakere(formDefinition.Content?.name || '', successCallback, errorCallback);
  };

  const promptLoginMessageFn = (): void => {
    if (shouldPromptLoginMessage) {
      dispatch(
        promptLoginMessage({
          questionnaire: formDefinition?.Content ?? undefined,
        })
      );
    }
  };
  const handleOnStepChange = (newStepIndex: number): void => {
    setStep(newStepIndex);
    if (!isMicroweb) {
      window.scrollTo(0, 0);
    }
  };

  return (
    <div>
      <StepViewComponent />
      <SkjemaTitle
        openSidebar={openSidebar}
        formDefinition={formDefinition}
        resources={resources}
        sidebarVisibility={uiState.sidebarVisibility}
      />
      {userMetadata && (
        <TopMessage
          resources={resources}
          authenticationRequirement={authenticationRequirement}
          userMetadata={userMetadata}
          printReference={uiState.printReference}
          openSidebar={openSidebar}
          visibility={uiState.topMessageVisibility}
        />
      )}
      <Navigator lastActivity={lastActivity} />
      <Refero
        componentPlugins={[
          visualCheckboxCloudPlugin,
          visualCheckboxCloudOpenChoicePlugin,
          visualCheckboxGroupPlugin,
          visualCheckboxGroupOpenChoicePlugin,
          visualRadioCloudPlugin,
          visualRadioCloudOpenChoicePlugin,
          visualRadioGroupOpenChoicePlugin,
          visualRadioGroupPlugin,
        ]}
        attachmentValidTypes={VALID_FILE_TYPES}
        authorized={allowAnonymousOrAuthorized}
        blockSubmit={blockSubmit}
        onSubmit={onSubmit}
        loginButton={<SkjemaLoginButton doNotRender={isMicroweb} buttonText={resources.skjemaLoginButton} />}
        resources={skjemaResources}
        promptLoginMessage={promptLoginMessageFn}
        onRequestAttachmentLink={getAttachmentLink}
        onOpenAttachment={openAttachment}
        onDeleteAttachment={deleteAttachment}
        uploadAttachment={uploadAttachment}
        attachmentErrorMessage={attachmentErrorMessage()}
        sticky={isMicroweb ? false : true}
        onRequestHelpButton={
          isMicroweb
            ? undefined
            : (
                _item: QuestionnaireItem,
                _itemHelp: QuestionnaireItem,
                _helpType: string,
                _helpText: string,
                opening: boolean
              ): React.JSX.Element => <HelpButton resources={resources} opening={opening} />
        }
        onRequestHelpElement={
          isMicroweb
            ? undefined
            : (
                _item: QuestionnaireItem,
                _itemHelp: QuestionnaireItem,
                _helpType: string,
                helpText: string,
                opening: boolean
              ): React.JSX.Element => <HelpElement helpText={helpText} opening={opening} />
        }
        validateScriptInjection
        onChange={registerActivity}
        fetchValueSet={loadValueSet}
        attachmentMaxFileSize={DEFAULT_MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_BYTES}
        autoSuggestProps={{
          minSearchCharacters: 3,
          typingSearchDelay: 500,
        }}
        submitButtonDisabled={submitButtonDisabled}
        fetchReceivers={fetchReceivers}
        onFieldsNotCorrectlyFilledOut={trackFormError}
        onStepChange={handleOnStepChange}
        hideValidationSummary={hideValidationSummary}
        renderCustomActionButtons={({
          reactHookFormMethods,
          nextStep,
          previousStep,
          displayNextButton,
          displayPreviousButton,
          setNewStepIndex,
        }) => (
          <RenderCustomFormButtons
            reactHookFormMethods={reactHookFormMethods}
            onCancel={onCancel}
            onSubmit={onSubmit}
            onSave={onSave}
            nextStep={nextStep}
            previousStep={previousStep}
            displayNextButton={displayNextButton}
            displayPreviousButton={displayPreviousButton}
            setNewStepIndex={setNewStepIndex}
          />
        )}
        onFormViewChange={(refElement, stepIndex) => handleFocus(refElement, isStepView, isMicrowebStep, stepIndex)}
      />
    </div>
  );
};
export default RenderSkjema;
